<template>
  <div class="supply-pratiche-section">
    <div v-if="isLoading" class="pratiche-loader">
      <i class="pi pi-spin pi-spinner"></i>
      <span>Caricamento pratiche...</span>
    </div>

    <prime-accordion v-else :multiple="true">
      <prime-accordion-tab :header="`Fornitori (${displayPayload.fornitori.length})`">
        <prime-data-table
          :paginator="true"
          :rows="10"
          :rowsPerPageOptions="[10, 25, 50]"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="{first} a {last} di {totalRecords} elementi"
          :value="displayPayload.fornitori"
          responsiveLayout="scroll"
        >
          <prime-column v-if="showPuntoColumn" field="punto" header="Punto"></prime-column>
          <prime-column header="Fornitore">
            <template #body="{ data }">
              <router-link
                v-if="linkFornitori && data.id"
                :to="`/ask/fornitori/${data.id}/dettagli`"
                custom
                v-slot="{ href }"
              >
                <prime-button
                  :label="data.fornitore || data.ragsoc || '-'"
                  class="p-button-info p-button-text"
                  @click="openLink(href)"
                />
              </router-link>
              <span v-else>{{ data.fornitore || data.ragsoc || '-' }}</span>
            </template>
          </prime-column>
          <prime-column field="tipo" header="Tipo"></prime-column>
          <prime-column header="Data Creazione">
            <template #body="{ data }">
              {{ formatDateTime(data.data_creazione) }}
            </template>
          </prime-column>
          <prime-column header="Data Aggiornamento">
            <template #body="{ data }">
              {{ formatDateTime(data.data_comunicazione) }}
            </template>
          </prime-column>
          <prime-column field="esito" :header="hasStatoColumn ? 'Esito' : 'Stato'"></prime-column>
          <prime-column v-if="hasStatoColumn" field="stato" header="Stato"></prime-column>
        </prime-data-table>
      </prime-accordion-tab>

      <prime-accordion-tab :header="`Pratiche Fornitore (${displayPayload.pratiche.length})`">
        <prime-data-table
          :paginator="true"
          :rows="10"
          :rowsPerPageOptions="[10, 25, 50]"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="{first} a {last} di {totalRecords} elementi"
          :value="displayPayload.pratiche"
          responsiveLayout="scroll"
        >
          <prime-column v-if="showPuntoColumn" field="punto" header="Punto"></prime-column>
          <prime-column field="name" header="Fornitura"></prime-column>
          <prime-column field="cod_servizio" header="Code servizio"></prime-column>
          <prime-column field="cod_pratica" header="Code pratica"></prime-column>
          <prime-column field="cod_pratica_provider" header="Code pratica Provider"></prime-column>
          <prime-column field="type" header="Tipo pratica"></prime-column>
          <prime-column field="esito" header="Esito"></prime-column>
          <prime-column field="motivazione_esito" header="Motivazione"></prime-column>
          <prime-column header="Data pratica">
            <template #body="{ data }">
              {{ formatDateTime(data.data_pratica) }}
            </template>
          </prime-column>
          <prime-column header="Data esito">
            <template #body="{ data }">
              {{ formatDateTime(data.data_esito) }}
            </template>
          </prime-column>
        </prime-data-table>
      </prime-accordion-tab>

      <prime-accordion-tab :header="`Pratiche Gestione (${displayPayload.pratiche_gestione.length})`">
        <prime-data-table
          :paginator="true"
          :rows="10"
          :rowsPerPageOptions="[10, 25, 50]"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="{first} a {last} di {totalRecords} elementi"
          :value="displayPayload.pratiche_gestione"
          responsiveLayout="scroll"
        >
          <prime-column v-if="showPuntoColumn" field="punto" header="Punto"></prime-column>
          <prime-column header="Tipologia">
            <template #body="{ data }">
              {{ data.tipologia || data.type_file || '-' }}
            </template>
          </prime-column>
          <prime-column header="Data Creazione">
            <template #body="{ data }">
              {{ formatPraticaGestioneDate(data.data_creazione || data.data) }}
            </template>
          </prime-column>
          <prime-column header="Ultimo Stato">
            <template #body="{ data }">
              {{ data.ultimo_stato || 'non lavorato' }}
            </template>
          </prime-column>
          <prime-column v-if="mostraColonnaRichiestaAppuntamento" header="Note">
            <template #body="{ data }">
              <!-- come "Ultima Nota" / "Nota Stato" del Portale delle pratiche, solo per le righe con il tasto -->
              <span v-if="data.tipo_pratica_appuntamento">{{ data.ultima_nota || '-' }}</span>
            </template>
          </prime-column>
          <prime-column v-if="mostraColonnaRichiestaAppuntamento" header="Richiesta Appuntamento">
            <template #body="{ data }">
              <!-- tipo_pratica_appuntamento arriva solo per le pratiche PDR previste (non Mc1, Voltura, Vt4) -->
              <appuntamento-pratica
                v-if="data.tipo_pratica_appuntamento"
                :pratica="data"
                :pratica-id="data.pratica_id"
                :tipo-pratica="data.tipo_pratica_appuntamento"
                :supply-code="data.supply_code"
                tooltip-con-testo
              />
            </template>
          </prime-column>
        </prime-data-table>
      </prime-accordion-tab>
    </prime-accordion>
  </div>
</template>

<script>
import moment from 'moment'
import PrimeAccordion from 'primevue/accordion'
import PrimeAccordionTab from 'primevue/accordiontab'
import PrimeDataTable from 'primevue/datatable'
import PrimeColumn from 'primevue/column'
import PrimeButton from 'primevue/button'
import PodsService from '@/services/ask-management/pods-service'
import PdrsService from '@/services/ask-management/pdrs-service'
import AppuntamentoPratica from '@/components/pratiche/gestione-pratiche/AppuntamentoPratica.vue'

const EMPTY_PAYLOAD = {
  fornitori: [],
  pratiche: [],
  pratiche_gestione: [],
}

export default {
  name: 'SupplyPraticheSection',

  components: {
    PrimeAccordion,
    PrimeAccordionTab,
    PrimeDataTable,
    PrimeColumn,
    PrimeButton,
    AppuntamentoPratica,
  },

  props: {
    supplyId: {
      type: [Number, String],
      default: null,
    },
    supplyType: {
      type: String,
      default: 'pod',
      validator: (value) => ['pod', 'pdr'].includes(value),
    },
    supplyPoints: {
      type: Array,
      default: () => [],
    },
    linkFornitori: {
      type: Boolean,
      default: false,
    },
    showPuntoColumn: {
      type: Boolean,
      default: false,
    },
    hasStatoColumn: {
      type: Boolean,
      default: false,
    },
    // colonna "Richiesta Appuntamento" in Pratiche Gestione (solo pagina In Call)
    showRichiestaAppuntamento: {
      type: Boolean,
      default: false,
    },
    praticheData: {
      type: Object,
      default: null,
    },
    autoFetch: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      loading: false,
      pratichePayload: { ...EMPTY_PAYLOAD },
    }
  },

  computed: {
    resolvedSupplyPoints() {
      if (Array.isArray(this.supplyPoints) && this.supplyPoints.length > 0) {
        return this.supplyPoints
          .map((point) => ({
            id: point.id,
            type: point.type === 'pdr' ? 'pdr' : 'pod',
            code: point.code || '',
          }))
          .filter((point) => point.id);
      }

      if (this.supplyId) {
        return [{
          id: this.supplyId,
          type: this.supplyType === 'pdr' ? 'pdr' : 'pod',
          code: '',
        }];
      }

      return [];
    },

    supplyPointsKey() {
      return this.resolvedSupplyPoints
        .map((point) => `${point.type}-${point.id}`)
        .join('|');
    },

    displayPayload() {
      if (this.praticheData) {
        return {
          fornitori: this.praticheData.fornitori || [],
          pratiche: this.praticheData.pratiche || [],
          pratiche_gestione: this.praticheData.pratiche_gestione || [],
        };
      }

      return this.pratichePayload;
    },

    // colonna solo se almeno una riga ha il tasto (pratiche PDR previste): con soli POD non compare
    mostraColonnaRichiestaAppuntamento() {
      return this.showRichiestaAppuntamento
        && this.displayPayload.pratiche_gestione.some((pratica) => pratica.tipo_pratica_appuntamento);
    },

    isLoading() {
      if (this.autoFetch) {
        return this.loading;
      }

      return this.praticheData === null;
    },
  },

  watch: {
    supplyPointsKey() {
      if (this.autoFetch) {
        this.loadPratiche();
      }
    },
    praticheData: {
      deep: true,
      handler(value) {
        if (value) {
          this.loading = false;
        }
      },
    },
  },

  beforeCreate() {
    this.podsService = new PodsService(this.$http, this.$eventBus);
    this.pdrsService = new PdrsService(this.$http, this.$eventBus);
  },

  mounted() {
    if (this.autoFetch) {
      this.loadPratiche();
    }
  },

  methods: {
    openLink(href) {
      window.open(href, '_blank');
    },

    formatDateTime(dateStr) {
      if (!dateStr) {
        return '';
      }

      const parsed = moment(dateStr, [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY'], true);
      return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
    },

    formatPraticaGestioneDate(dateStr) {
      if (!dateStr) {
        return '-';
      }

      const parsed = moment(dateStr, ['DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
      return parsed.isValid() ? parsed.format('DD/MM/YYYY') : dateStr;
    },

    withPunto(items, punto) {
      if (!this.showPuntoColumn || !punto) {
        return Array.isArray(items) ? items : [];
      }

      return (Array.isArray(items) ? items : []).map((item) => ({
        ...item,
        punto,
      }));
    },

    async loadPratiche() {
      const points = this.resolvedSupplyPoints;

      if (!points.length) {
        this.pratichePayload = { ...EMPTY_PAYLOAD };
        return;
      }

      this.loading = true;

      try {
        const responses = await Promise.all(
          points.map(async (point) => {
            const service = point.type === 'pdr' ? this.pdrsService : this.podsService;
            const result = await service.getPraticheFornitori(point.id);
            const data = result?.item || {};

            return {
              punto: point.code || `${point.type.toUpperCase()} ${point.id}`,
              fornitori: this.withPunto(data.fornitori, point.code),
              pratiche: this.withPunto(data.pratiche, point.code),
              pratiche_gestione: this.withPunto(data.pratiche_gestione, point.code),
            };
          })
        );

        this.pratichePayload = responses.reduce((acc, current) => {
          acc.fornitori.push(...current.fornitori);
          acc.pratiche.push(...current.pratiche);
          acc.pratiche_gestione.push(...current.pratiche_gestione);
          return acc;
        }, { ...EMPTY_PAYLOAD, fornitori: [], pratiche: [], pratiche_gestione: [] });
      } catch (error) {
        console.error(error);
        this.pratichePayload = { ...EMPTY_PAYLOAD };
      } finally {
        this.loading = false;
      }
    },
  },
}
</script>

<style scoped>
.supply-pratiche-section {
  width: 100%;
}

.pratiche-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem;
  background: white;
  color: #666;
}
</style>
